import type { DownloadStepperOption } from 'components/molecules/common/DownloadStepperModal';
import type { StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { FilterContextData } from 'contexts/FilterContext';
import type { WhitespaceGridRow, WhitespaceGridCell } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { downloadExcel } from 'utils/ppaHelpers';
import { downloadCsv, formatWord, sendNotification } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_EXPORT_CLICKED,
    PPA_WHITESPACE_EXPORT_CONFIRMED,
    PPA_WHITESPACE_EXPORT_FAILED,
    PPA_WHITESPACE_EXPORT_MODAL_VIEWED,
    PPA_WHITESPACE_EXPORT_SCOPE_SELECTED,
    PPA_WHITESPACE_EXPORT_SUCCEEDED,
} from 'utils/mixpanelEventNames';
import {
    buildWhitespaceExportRows,
    type WhitespaceExportFormat,
    type WhitespaceExportScope,
} from './whitespaceExportConfig';
import type { computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';

type GridMetrics = ReturnType<typeof computeWhitespaceGridMetrics>;

export const WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS: DownloadStepperOption[] = [
    { value: 'current_view', label: 'Current View' },
];

export const WHITESPACE_DOWNLOAD_STEP_TWO_OPTIONS: DownloadStepperOption[] = [
    { value: 'xlsx', label: 'download as .xlsx' },
    { value: 'csv', label: 'download as .csv' },
];

type CellPaginationData = {
    items: StaticWhitespaceCardProps['items'];
    moreCount: number;
};

type UseWhitespaceExportParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    incrementValue: number;
    showWhitespaces: boolean;
    comparisonMode: string;
    gridMetrics: GridMetrics;
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    selectedYAxisKpiId: string;
    getCellPaginationData: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => CellPaginationData;
};

const prepareWhitespaceExportGridRows = (
    gridRows: WhitespaceGridRow[],
    columnHeaders: string[],
    getCellPaginationData: UseWhitespaceExportParams['getCellPaginationData'],
): WhitespaceGridRow[] =>
    gridRows.map((row) => {
        const cells = columnHeaders.reduce<Record<string, WhitespaceGridCell | null>>((acc, columnHeader) => {
            const cell = row.cells[columnHeader];
            if (!cell) {
                acc[columnHeader] = cell ?? null;
                return acc;
            }
            const paginationData = getCellPaginationData({
                bucketLabel: row.bucketLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            acc[columnHeader] = {
                ...cell,
                items: paginationData.items,
                moreCount: paginationData.moreCount,
            };
            return acc;
        }, {});
        return { ...row, cells };
    });

const buildWhitespaceExportMetadataRows = ({
    allCheckedAttribute,
    xAxis,
    yAxis,
    crossSection,
    incrementValue,
    showWhitespaces,
}: {
    allCheckedAttribute: Record<string, unknown> | undefined;
    xAxis: string;
    yAxis: string;
    crossSection: string;
    incrementValue: number;
    showWhitespaces: boolean;
}): Array<Array<string | number>> => {
    const appliedFilterGroups = Object.entries(allCheckedAttribute || {})
        .map(([attributeType, selectedValues]) => ({
            label: formatWord(attributeType),
            items: Object.keys((selectedValues as Record<string, unknown>) || {}),
        }))
        .filter((group) => group.items.length > 0);
    const globalFiltersValue = appliedFilterGroups
        .map((group) => `${group.label}: ${group.items.join(', ')}`)
        .join(' | ');

    return [
        ['Global filters', globalFiltersValue || 'No global filters selected'],
        ['X-Axis', xAxis || '-'],
        ['Y-Axis', yAxis || '-'],
        ['Cross Section', crossSection || '-'],
        ['Y-Axis increment', incrementValue],
        ['Show Whitespaces toggle', showWhitespaces ? 'ON' : 'OFF'],
        [],
    ];
};

const writeWhitespaceExportFile = async (
    format: WhitespaceExportFormat,
    fileName: string,
    exportRows: Array<Array<string | number>>,
): Promise<void> => {
    if (format === 'xlsx') {
        await downloadExcel(fileName, exportRows, { moduleKey: 'whitespace_analysis' });
        return;
    }

    const csvEscape = (value: string | number) => {
        const stringValue = String(value ?? '');
        if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };
    const csvContent = exportRows.map((row) => row.map(csvEscape).join(',')).join('\n');
    downloadCsv([csvContent], `${fileName}.csv`);
};

const useWhitespaceExport = ({
    xAxis,
    yAxis,
    crossSection,
    incrementValue,
    showWhitespaces,
    comparisonMode,
    gridMetrics,
    columnHeaders,
    gridRows,
    selectedYAxisKpiId,
    getCellPaginationData,
}: UseWhitespaceExportParams) => {
    const { allCheckedAttribute } = useContext(FilterContextData);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const previousExportScopeRef = useRef<string | null>(
        WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS[0]?.value ?? null,
    );

    const handleOpenDownloadModal = useCallback(() => {
        trackEventMixpanel(PPA_WHITESPACE_EXPORT_CLICKED, {
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            increment_value: incrementValue,
            show_whitespaces: showWhitespaces,
            comparison_mode: comparisonMode,
            total_cell_count: gridMetrics.total_cell_count,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        });
        setIsDownloadModalOpen(true);
    }, [
        comparisonMode,
        crossSection,
        gridMetrics.opportunity_cell_count,
        gridMetrics.total_cell_count,
        incrementValue,
        showWhitespaces,
        xAxis,
        yAxis,
    ]);

    const handleCloseDownloadModal = useCallback(() => setIsDownloadModalOpen(false), []);

    useEffect(() => {
        if (!isDownloadModalOpen) return;
        trackEventMixpanel(PPA_WHITESPACE_EXPORT_MODAL_VIEWED, {
            available_export_scopes: WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS.map((option) => option.value),
            default_export_scope: WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS[0]?.value ?? 'current_view',
            supported_formats: WHITESPACE_DOWNLOAD_STEP_TWO_OPTIONS.map((option) => option.value),
            total_cell_count: gridMetrics.total_cell_count,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        });
    }, [gridMetrics.opportunity_cell_count, gridMetrics.total_cell_count, isDownloadModalOpen]);

    const handleExportScopeChange = useCallback(
        (selectedScope: string, previousScope: string | null) => {
            const exportGridRows = gridRows.map((row) => ({ ...row, cells: { ...row.cells } }));
            const { rows } = buildWhitespaceExportRows({
                gridRows: exportGridRows,
                columnHeaders,
                crossSection,
                scope: selectedScope as WhitespaceExportScope,
                selectedYAxisKpiId,
            });

            trackEventMixpanel(PPA_WHITESPACE_EXPORT_SCOPE_SELECTED, {
                previous_export_scope:
                    previousScope ?? WHITESPACE_DOWNLOAD_STEP_ONE_OPTIONS[0]?.value ?? 'current_view',
                selected_export_scope: selectedScope,
                estimated_cell_count: gridMetrics.visible_cell_count,
                estimated_product_row_count: rows.length,
            });
            previousExportScopeRef.current = selectedScope;
        },
        [columnHeaders, crossSection, gridMetrics.visible_cell_count, gridRows, selectedYAxisKpiId],
    );

    const handleDownloadExport = useCallback(
        async ({ stepOneValue, stepTwoValue }: { stepOneValue: string; stepTwoValue: string }) => {
            const scope = stepOneValue as WhitespaceExportScope;
            const format = stepTwoValue as WhitespaceExportFormat;
            const exportStartedAt = performance.now();

            trackEventMixpanel(PPA_WHITESPACE_EXPORT_CONFIRMED, {
                export_scope: scope,
                export_format: format,
                x_axis: xAxis,
                y_axis: yAxis,
                cross_section: crossSection,
                increment_value: incrementValue,
                show_whitespaces: showWhitespaces,
                comparison_mode: comparisonMode,
            });

            const exportGridRows = prepareWhitespaceExportGridRows(gridRows, columnHeaders, getCellPaginationData);
            const { headers, rows } = buildWhitespaceExportRows({
                gridRows: exportGridRows,
                columnHeaders,
                crossSection,
                scope,
                selectedYAxisKpiId,
            });

            if (rows.length === 0) {
                sendNotification('info', 'No data available to export for the selected option.');
                return;
            }

            const fileName = `whitespace_analysis_${new Date().toISOString().slice(0, 10)}`;
            const exportMetadataRows = buildWhitespaceExportMetadataRows({
                allCheckedAttribute,
                xAxis,
                yAxis,
                crossSection,
                incrementValue,
                showWhitespaces,
            });
            const exportRows = [...exportMetadataRows, headers, ...rows];

            try {
                await writeWhitespaceExportFile(format, fileName, exportRows);

                trackEventMixpanel(PPA_WHITESPACE_EXPORT_SUCCEEDED, {
                    export_scope: scope,
                    export_format: format,
                    file_name: format === 'xlsx' ? `${fileName}.xlsx` : `${fileName}.csv`,
                    exported_cell_count: gridMetrics.visible_cell_count,
                    exported_product_row_count: rows.length,
                    sheet_count: 1,
                    duration_ms: Math.round(performance.now() - exportStartedAt),
                });

                if (scope === 'all_filtered_cells') {
                    sendNotification(
                        'info',
                        'Export includes currently loaded grid data. Scroll/load more rows and columns to include additional cells.',
                    );
                } else {
                    sendNotification('success', 'Export completed.');
                }
                setIsDownloadModalOpen(false);
            } catch (error) {
                trackEventMixpanel(PPA_WHITESPACE_EXPORT_FAILED, {
                    export_scope: scope,
                    export_format: format,
                    error_type: error instanceof Error ? error.name : 'export_error',
                    error_message: error instanceof Error ? error.message : String(error),
                    failed_request_id: '-',
                    retry_available: true,
                });
                sendNotification('error', 'Failed to export whitespace data. Please try again.');
            }
        },
        [
            allCheckedAttribute,
            columnHeaders,
            comparisonMode,
            crossSection,
            getCellPaginationData,
            gridMetrics.visible_cell_count,
            gridRows,
            incrementValue,
            selectedYAxisKpiId,
            showWhitespaces,
            xAxis,
            yAxis,
        ],
    );

    return {
        isDownloadModalOpen,
        handleOpenDownloadModal,
        handleCloseDownloadModal,
        handleExportScopeChange,
        handleDownloadExport,
    };
};

export default useWhitespaceExport;
